// CoordStaffSalaryMonth.jsx — matches admin TeacherSalaryMonth exactly
// Add Day → request, Edit day → request, Finalize → request
// If staffId === myUserId (own salary): export only, no Add/Edit/Finalize

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon, BanknotesIcon, CalendarDaysIcon, ArrowDownTrayIcon,
  LockClosedIcon, PencilSquareIcon, XMarkIcon, PlusCircleIcon,
  GiftIcon, ClockIcon, ExclamationTriangleIcon, PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .tm-root * { box-sizing:border-box; }
  .tm-root { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tmFade .4s ease both; }
  @keyframes tmFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .tm-back { display:inline-flex; align-items:center; gap:6px; font-size:.78rem; font-weight:600; color:#7880a4; cursor:pointer; margin-bottom:1.4rem; border:none; background:none; font-family:'Sora',sans-serif; padding:0; transition:color .15s; }
  .tm-back:hover { color:#3b6ef8; }
  .tm-back svg { width:14px; height:14px; stroke-width:2.5; }
  .tm-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .tm-title-wrap { display:flex; align-items:center; gap:12px; }
  .tm-avatar { width:46px; height:46px; border-radius:13px; background:#ecfdf5; color:#10c98e; display:flex; align-items:center; justify-content:center; font-size:.95rem; font-weight:700; text-transform:uppercase; flex-shrink:0; }
  .tm-name  { font-size:1.25rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .tm-month { font-size:.75rem; color:#9094a8; margin-top:2px; }
  .tm-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; } .btn-blue:hover  { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); }
  .btn-green { background:#10c98e; color:white; } .btn-green:hover { background:#0db87e; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .export-wrap { position:relative; display:inline-flex; }
  .export-menu { position:absolute; top:calc(100% + 6px); right:0; background:white; border:1px solid #e8ebf4; border-radius:12px; box-shadow:0 8px 24px rgba(0,0,0,.1); min-width:160px; z-index:100; overflow:hidden; }
  .export-menu-item { display:flex; align-items:center; gap:8px; padding:9px 14px; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; color:#1a1d2e; cursor:pointer; border:none; background:none; width:100%; text-align:left; transition:background .12s; }
  .export-menu-item:hover { background:#f5f7ff; color:#3b6ef8; }
  .export-menu-item svg { width:14px; height:14px; stroke-width:2; color:#9094a8; }
  .export-menu-sep   { height:1px; background:#f0f2f7; margin:4px 0; }
  .export-menu-label { font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; padding:6px 14px 2px; }
  .req-banner { border-radius:12px; padding:10px 16px; margin-bottom:1.25rem; font-size:.78rem; font-weight:600; background:#fff7ed; color:#9a3412; border:1px solid #fed7aa; }
  .finalized-banner { display:flex; align-items:center; gap:10px; padding:12px 16px; border-radius:12px; margin-bottom:1.25rem; font-size:.8rem; font-weight:600; flex-wrap:wrap; }
  .finalized-banner.green { background:#ecfdf5; color:#065f46; border:1px solid #a7f3d0; }
  .finalized-banner.blue  { background:#eef2ff; color:#3730a3; border:1px solid #c7d2fe; }
  .finalized-banner svg   { width:16px; height:16px; stroke-width:2; flex-shrink:0; }
  .fb-meta { font-size:.7rem; font-weight:400; color:#6b7194; margin-top:1px; }
  .stat-grid { display:grid; grid-template-columns:repeat(6,1fr); gap:.9rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.1rem; border:1px solid #e8ebf4; }
  .s-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; margin-bottom:.7rem; }
  .s-icon svg { width:15px; height:15px; stroke-width:1.8; }
  .s-icon.blue{background:#eef2ff;color:#3b6ef8} .s-icon.green{background:#ecfdf5;color:#10c98e}
  .s-icon.amber{background:#fffbeb;color:#f59e0b} .s-icon.red{background:#fef2f2;color:#ef4444}
  .s-icon.purple{background:#f5f3ff;color:#8b5cf6}
  .s-label { font-size:.68rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:2px; }
  .s-value { font-size:1.1rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; }
  .s-sub   { font-size:.68rem; color:#b0b5cc; margin-top:3px; }
  .table-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1.4rem; }
  .table-panel-header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .tp-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .tp-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .day-table { width:100%; border-collapse:collapse; }
  .day-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .day-table th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .day-table th.right { text-align:right; }
  .day-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .day-table tbody tr:last-child { border-bottom:none; }
  .day-table tbody tr:hover { background:#fafbff; }
  .day-table td { padding:10px 14px; vertical-align:middle; font-size:.79rem; text-align:center !important; }
  .day-table td.right { text-align:right; }
  .day-date { font-family:'JetBrains Mono',monospace; font-size:.78rem; font-weight:500; color:#1a1d2e; }
  .day-dow  { font-size:.67rem; color:#9094a8; margin-top:1px; }
  .total-row td { background:#fafbff; font-weight:700; border-top:2px solid #e8ebf4 !important; text-align:center !important; }
  .day-cards { display:none; padding:10px; }
  .day-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:12px 13px; margin-bottom:8px; }
  .dc-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .dc-date { font-family:'JetBrains Mono',monospace; font-size:.8rem; font-weight:600; color:#0f1123; }
  .dc-dow  { font-size:.67rem; color:#9094a8; }
  .dc-row  { display:flex; align-items:center; justify-content:space-between; font-size:.75rem; padding:3px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .dc-row:last-of-type { border-bottom:none; }
  .dc-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .dc-net { font-family:'JetBrains Mono',monospace; font-size:.9rem; font-weight:700; color:#10c98e; }
  .day-total-card { background:white; border-radius:12px; border:2px solid #e8ebf4; padding:13px; margin:0 10px 10px; }
  .dtc-row { display:flex; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f3f5fb; gap:8px; }
  .dtc-row:last-child { border-bottom:none; }
  .dtc-label { color:#9094a8; font-weight:600; }
  .status-pill { display:inline-flex; align-items:center; font-size:.67rem; font-weight:600; padding:2px 9px; border-radius:99px; }
  .status-pill.present  { background:#ecfdf5; color:#10c98e; }
  .status-pill.absent   { background:#fef2f2; color:#ef4444; }
  .status-pill.on-leave { background:#eef2ff; color:#3b6ef8; }
  .status-pill.holiday  { background:#f3f0ff; color:#8b5cf6; }
  .status-pill.off      { background:#f0f2f7; color:#6b7194; }
  .mono-sm  { font-family:'JetBrains Mono',monospace; font-size:.75rem; color:#6b7194; }
  .ot-badge { display:inline-flex; font-family:'JetBrains Mono',monospace; font-size:.68rem; font-weight:600; padding:2px 7px; border-radius:99px; background:#f3f0ff; color:#8b5cf6; }
  .amt        { font-family:'JetBrains Mono',monospace; font-size:.78rem; font-weight:600; color:#1a1d2e; }
  .amt.red    { color:#ef4444; }
  .amt.green  { color:#10c98e; font-size:.82rem; }
  .amt.dim    { color:#c0c5dc; font-weight:400; }
  .amt.purple { color:#8b5cf6; }
  .amt.teal   { color:#06b6d4; }
  .edit-btn { width:28px; height:28px; border-radius:7px; border:1px solid #e4e7f0; background:white; color:#9094a8; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
  .edit-btn:hover { background:#eef2ff; border-color:#3b6ef8; color:#3b6ef8; }
  .edit-btn svg { width:13px; height:13px; stroke-width:2; }
  .tm-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px; overflow-y:auto; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; margin:auto; max-height:90vh; overflow-y:auto; }
  .modal-sm { max-width:420px; }
  .modal-lg { max-width:520px; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; gap:8px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:30px; height:30px; border-radius:8px; border:none; background:#f0f2f7; color:#6b7194; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:15px; height:15px; stroke-width:2; }
  .modal-sub  { font-size:.78rem; color:#6b7194; line-height:1.55; margin-bottom:16px; margin-top:-10px; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:20px; flex-wrap:wrap; }
  .edit-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .edit-field { display:flex; flex-direction:column; gap:5px; }
  .edit-field.span2 { grid-column:1/-1; }
  .edit-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .edit-input,.edit-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .edit-input:focus,.edit-select:focus { border-color:#3b6ef8; background:white; }
  .edit-hint { font-size:.67rem; color:#b0b5cc; }
  @media (max-width:1200px) { .stat-grid { grid-template-columns:repeat(3,1fr); } }
  @media (max-width:768px) {
    .tm-name { font-size:1.05rem; }
    .stat-grid { grid-template-columns:repeat(2,1fr); }
    .day-table-wrap { display:none !important; }
    .day-cards { display:block; }
    .modal { padding:18px; }
    .edit-grid { grid-template-columns:1fr; }
    .edit-field.span2 { grid-column:1; }
    .modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
  }
`;

const initials  = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase() || '?';
const fmt       = (n)    => `Rs. ${(n||0).toLocaleString('en-PK',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtTime   = (d)    => d ? new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '—';
const fmtDate   = (d)    => new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short'});
const dayOfWeek = (d)    => new Date(d).toLocaleDateString('en-US',{weekday:'short'});
const monthLabel= (m)    => { const[y,mn]=m.split('-').map(Number); return new Date(y,mn-1,1).toLocaleDateString('en-US',{month:'long',year:'numeric'}); };
const toTime24  = (d)    => { if(!d)return''; const dt=new Date(d); return `${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`; };

const exportCSV = (rows, filename) => {
  if (!rows.length) return;
  const hdrs = Object.keys(rows[0]);
  const esc  = v => { const s=v==null?'':String(v); return s.includes(',')||s.includes('"')?'"'+s.replace(/"/g,'""')+'"':s; };
  const csv  = [hdrs.join(','), ...rows.map(r=>hdrs.map(h=>esc(r[h])).join(','))].join('\n');
  const a    = document.createElement('a');
  a.href     = URL.createObjectURL(new Blob([csv],{type:'text/csv;charset=utf-8;'}));
  a.download = filename+'.csv'; a.click();
};
const exportXLSX = async (rows, filename) => {
  try { const X=await import('xlsx'); const ws=X.utils.json_to_sheet(rows); const wb=X.utils.book_new(); X.utils.book_append_sheet(wb,ws,'Sheet1'); X.writeFile(wb,filename+'.xlsx'); }
  catch { exportCSV(rows, filename); }
};

export default function CoordStaffSalaryMonth() {
  const { staffId, month } = useParams();
  const [searchParams]     = useSearchParams();
  const role     = searchParams.get('role') || 'teacher';
  const isPortal = searchParams.get('isPortal') !== 'false';
  const navigate = useNavigate();

  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [myUserId,   setMyUserId]   = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [saving,     setSaving]     = useState(false);

  // Edit day modal
  const [editDay,  setEditDay]  = useState(null);
  const [editForm, setEditForm] = useState({});
  // Add day modal
  const [addOpen,  setAddOpen]  = useState(false);
  const [addForm,  setAddForm]  = useState({ date:'', checkIn:'', checkOut:'', lateMinutes:'0', earlyExitMinutes:'0', status:'present' });
  // Finalize modal
  const [finalizeOpen, setFinalizeOpen] = useState(false);

  useEffect(() => {
    api.get('/coordinator/me').then(r => setMyUserId(String(r.data?._id||''))).catch(()=>{});
  }, []);

  useEffect(() => { fetchDetail(); }, [staffId, month]);

  useEffect(() => {
    if (!exportOpen) return;
    const h = () => setExportOpen(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [exportOpen]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const { data: res } = await api.get(`/coordinator/staff-salary-detail/${staffId}/${month}?role=${role}&isPortal=${isPortal}`);
      setData(res);
    } catch { toast.error('Failed to load month detail'); }
    finally  { setLoading(false); }
  };

  const isOwnSalary = myUserId && String(staffId) === String(myUserId);

  const openEdit = (day) => {
    setEditDay(day);
    setEditForm({
      status:           day.status,
      checkIn:          toTime24(day.checkIn),
      checkOut:         toTime24(day.checkOut),
      lateMinutes:      String(day.lateMinutes||0),
      earlyExitMinutes: String(day.earlyMinutes||0),
      incentive:        String(day.incentive||0),
    });
  };

  const submitEditRequest = async () => {
    if (!editDay) return;
    setSaving(true);
    try {
      await api.post('/coordinator/request/staff-attendance-edit', {
        staffId, staffName: data?.teacher?.name, role, isPortal,
        date:             new Date(editDay.date).toISOString().slice(0,10),
        month,
        attendanceId:     editDay.attendanceId,
        status:           editForm.status,
        checkIn:          editForm.checkIn,
        checkOut:         editForm.checkOut,
        lateMinutes:      Number(editForm.lateMinutes)||0,
        earlyExitMinutes: Number(editForm.earlyExitMinutes)||0,
        incentive:        Number(editForm.incentive)||0,
        note:             `Edit from salary month detail — ${monthLabel(month)}`,
      });
      toast.success('Edit request submitted — awaiting admin approval');
      setEditDay(null);
    } catch { toast.error('Failed to submit request'); }
    finally { setSaving(false); }
  };

  const submitAddRequest = async () => {
    if (!addForm.date) { toast.error('Please select a date'); return; }
    setSaving(true);
    try {
      await api.post('/coordinator/request/staff-attendance-edit', {
        staffId, staffName: data?.teacher?.name, role, isPortal,
        date:             addForm.date,
        month,
        attendanceId:     null,
        status:           addForm.status,
        checkIn:          addForm.checkIn,
        checkOut:         addForm.checkOut,
        lateMinutes:      Number(addForm.lateMinutes)||0,
        earlyExitMinutes: Number(addForm.earlyExitMinutes)||0,
        incentive:        0,
        note:             `Add day request — ${monthLabel(month)}`,
      });
      toast.success('Add day request submitted — awaiting admin approval');
      setAddOpen(false);
      setAddForm({ date:'', checkIn:'', checkOut:'', lateMinutes:'0', earlyExitMinutes:'0', status:'present' });
    } catch { toast.error('Failed to submit request'); }
    finally { setSaving(false); }
  };

  const submitFinalizeRequest = async () => {
    setSaving(true);
    try {
      await api.post('/coordinator/request/salary-finalize', {
        staffId, staffName: data?.teacher?.name, employeeId: data?.teacher?.employeeId,
        role, isPortal, month,
      });
      toast.success('Finalize request submitted — awaiting admin approval');
      setFinalizeOpen(false);
    } catch { toast.error('Failed to submit request'); }
    finally { setSaving(false); }
  };

  const handleExport = async (scope, fmt_) => {
    setExportOpen(false);
    const name = data?.teacher?.name?.replace(/\s+/g,'-')||'staff';
    if (scope === 'daily') {
      const rows = (data?.days||[]).map(d => ({
        Date: fmtDate(d.date), Day: dayOfWeek(d.date), Status: d.status,
        'Check In':  fmtTime(d.checkIn), 'Check Out': fmtTime(d.checkOut),
        'Work Hrs':  d.workHours?.toFixed(2),
        'Late Min':  d.lateMinutes||0, 'Early Exit Min': d.earlyMinutes||0,
        'Incentive': (d.incentive||0).toFixed(2),
        'Regular Pay': d.regularPay?.toFixed(2), 'OT Pay': d.overtimePay?.toFixed(2),
        'Deductions':  d.deductions?.toFixed(2),  'Net Day': d.netDay?.toFixed(2),
      }));
      if (!rows.length) { toast.error('No data to export'); return; }
      fmt_==='csv' ? exportCSV(rows,`salary-daily-${name}-${month}`) : await exportXLSX(rows,`salary-daily-${name}-${month}`);
    } else {
      const t = data?.totals||{};
      const rows = [{ Name:data?.teacher?.name, Month:month, Present:t.daysPresent, Absent:t.daysAbsent, 'Total Hrs':t.totalHours, 'OT Hrs':t.totalOvertimeHours, 'Late Min':t.totalLateMinutes, Deductions:(t.deductions||0).toFixed(2), 'Net Salary':(t.netSalary||0).toFixed(2), Status:data?.savedRecord?.status||'not finalized' }];
      fmt_==='csv' ? exportCSV(rows,`salary-summary-${name}-${month}`) : await exportXLSX(rows,`salary-summary-${name}-${month}`);
    }
  };

  if (loading) return <div className="tm-root"><style>{style}</style><div className="tm-loading"><div className="spinner"/>Loading…</div></div>;
  if (!data)   return null;

  const { teacher, days, totals, savedRecord } = data;
  const isFinalized     = !!savedRecord;
  const totalIncentives = totals.incentives ?? days.reduce((s,d) => s+(d.incentive||0), 0);

  return (
    <div className="tm-root"><style>{style}</style>

      <button className="tm-back" onClick={() => navigate(`/coordinator/staff-salary-year/${staffId}?role=${role}&isPortal=${isPortal}`)}>
        <ChevronLeftIcon />Back to Months
      </button>

      <div className="tm-header">
        <div className="tm-title-wrap">
          <div className="tm-avatar">{initials(teacher?.name)}</div>
          <div>
            <div className="tm-name">{teacher?.name}</div>
            <div className="tm-month">{monthLabel(month)}{teacher?.employeeId ? ` · ${teacher.employeeId}` : ''}</div>
          </div>
        </div>
        <div className="tm-actions">
          {/* Export — always available */}
          <div className="export-wrap" onMouseDown={e => e.stopPropagation()}>
            <button className="btn btn-ghost" onClick={() => setExportOpen(o => !o)}>
              <ArrowDownTrayIcon />Export
            </button>
            {exportOpen && (
              <div className="export-menu">
                <div className="export-menu-label">Day-by-Day</div>
                <button className="export-menu-item" onClick={() => handleExport('daily','csv')}><ArrowDownTrayIcon />CSV</button>
                <button className="export-menu-item" onClick={() => handleExport('daily','xlsx')}><ArrowDownTrayIcon />XLSX</button>
                <div className="export-menu-sep"/>
                <div className="export-menu-label">Monthly Summary</div>
                <button className="export-menu-item" onClick={() => handleExport('summary','csv')}><ArrowDownTrayIcon />CSV</button>
                <button className="export-menu-item" onClick={() => handleExport('summary','xlsx')}><ArrowDownTrayIcon />XLSX</button>
              </div>
            )}
          </div>

          {/* Add Day + Finalize — only for other staff, not own salary */}
          {!isOwnSalary && (
            <>
              <button className="btn btn-green" onClick={() => setAddOpen(true)}>
                <PlusCircleIcon />Request Add Day
              </button>
              <button className="btn btn-blue" onClick={() => setFinalizeOpen(true)}>
                <LockClosedIcon />Request Finalize
              </button>
            </>
          )}
        </div>
      </div>

      {!isOwnSalary && (
        <div className="req-banner">📋 Add Day, Edit, and Finalize actions are submitted as requests — admin must approve before they take effect.</div>
      )}

      {isOwnSalary && (
        <div className="req-banner" style={{background:'#f0f2f7',color:'#6b7194',border:'1px solid #e0e4f0'}}>👁 Viewing your own salary — read-only. Use My Salary page to see your full breakdown.</div>
      )}

      {isFinalized && (
        <div className={`finalized-banner ${savedRecord.status==='paid' ? 'blue' : 'green'}`}>
          {savedRecord.status==='paid' ? <BanknotesIcon/> : <LockClosedIcon/>}
          <div>
            <div>{savedRecord.status==='paid' ? 'Salary paid' : 'Finalized — awaiting payment'}</div>
            <div className="fb-meta">Finalized on {fmtDate(savedRecord.finalizedAt)} by {savedRecord.finalizedBy}</div>
          </div>
        </div>
      )}

      {/* Stat grid */}
      <div className="stat-grid">
        <div className="stat-card"><div className="s-icon blue"><CalendarDaysIcon/></div><div className="s-label">Present</div><div className="s-value">{totals.daysPresent}</div><div className="s-sub">{totals.daysAbsent} absent</div></div>
        <div className="stat-card"><div className="s-icon green"><ClockIcon/></div><div className="s-label">Total Hours</div><div className="s-value">{totals.totalHours}</div><div className="s-sub">{totals.totalOvertimeHours} overtime</div></div>
        <div className="stat-card"><div className="s-icon amber"><ExclamationTriangleIcon/></div><div className="s-label">Late Minutes</div><div className="s-value">{totals.totalLateMinutes}</div><div className="s-sub">{totals.totalEarlyMinutes} early exit</div></div>
        <div className="stat-card"><div className="s-icon purple"><GiftIcon/></div><div className="s-label">Incentives</div><div className="s-value" style={{fontSize:'.9rem',color:'#8b5cf6'}}>{fmt(totalIncentives)}</div></div>
        <div className="stat-card"><div className="s-icon red"><BanknotesIcon/></div><div className="s-label">Deductions</div><div className="s-value" style={{fontSize:'.9rem'}}>{fmt(totals.deductions)}</div></div>
        <div className="stat-card"><div className="s-icon green"><BanknotesIcon/></div><div className="s-label">Net Salary</div><div className="s-value" style={{fontSize:'.9rem',color:'#10c98e'}}>{fmt(totals.netSalary)}</div></div>
      </div>

      <div className="table-panel">
        <div className="table-panel-header">
          <div><div className="tp-title">Day-by-Day Breakdown</div><div className="tp-sub">{days.length} records</div></div>
        </div>

        {days.length === 0 ? (
          <div style={{textAlign:'center',padding:'3rem',color:'#9094a8',fontSize:'.82rem'}}>No attendance records for this month.</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="day-table-wrap" style={{overflowX:'auto'}}>
              <table className="day-table">
                <thead><tr>
                  <th>Date</th><th>Status</th><th>Check In</th><th>Check Out</th>
                  <th>Hours</th><th>Overtime</th><th>Late</th><th>Early Exit</th>
                  <th>Regular Pay</th><th>OT Pay</th><th style={{color:'#8b5cf6'}}>Incentive</th>
                  <th>Deductions</th><th className="right">Net</th>
                  {!isOwnSalary && <th></th>}
                </tr></thead>
                <tbody>
                  {days.map((d, i) => (
                    <tr key={i}>
                      <td><div className="day-date">{fmtDate(d.date)}</div><div className="day-dow">{dayOfWeek(d.date)}</div></td>
                      <td><span className={`status-pill ${d.status}`}>{d.status}</span></td>
                      <td><span className="mono-sm">{fmtTime(d.checkIn)}</span></td>
                      <td><span className="mono-sm">{fmtTime(d.checkOut)}</span></td>
                      <td><span className="mono-sm">{d.workHours?.toFixed(2)} hrs</span></td>
                      <td>{d.overtimeHours>0?<span className="ot-badge">+{d.overtimeHours?.toFixed(2)}</span>:<span className="amt dim">—</span>}</td>
                      <td><span className="mono-sm" style={{color:d.lateMinutes>0?'#f59e0b':'#b0b5cc'}}>{d.lateMinutes>0?`${d.lateMinutes}m`:'—'}</span></td>
                      <td><span className="mono-sm" style={{color:d.earlyMinutes>0?'#ef4444':'#b0b5cc'}}>{d.earlyMinutes>0?`${d.earlyMinutes}m`:'—'}</span></td>
                      <td><span className="amt">{fmt(d.regularPay)}</span></td>
                      <td>{d.overtimePay>0?<span className="amt purple">{fmt(d.overtimePay)}</span>:<span className="amt dim">—</span>}</td>
                      <td>{(d.incentive||0)>0?<span className="amt teal">{fmt(d.incentive)}</span>:<span className="amt dim">—</span>}</td>
                      <td>{d.deductions>0?<span className="amt red">−{fmt(d.deductions)}</span>:<span className="amt dim">—</span>}</td>
                      <td className="right"><span className="amt green">{fmt(d.netDay)}</span></td>
                      {!isOwnSalary && <td><button className="edit-btn" onClick={() => openEdit(d)} title="Request Edit"><PencilSquareIcon /></button></td>}
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td colSpan={4}><span style={{fontFamily:'Sora',fontWeight:700,fontSize:'.8rem',color:'#0f1123'}}>Monthly Total</span><div style={{fontSize:'.67rem',color:'#9094a8',marginTop:2}}>{totals.daysPresent}P · {totals.daysAbsent}A · {totals.daysOnLeave||0}L</div></td>
                    <td><span className="mono-sm" style={{fontWeight:700,color:'#0f1123'}}>{totals.totalHours} hrs</span></td>
                    <td>{totals.totalOvertimeHours>0?<span className="ot-badge">+{totals.totalOvertimeHours}</span>:<span className="amt dim">—</span>}</td>
                    <td><span className="mono-sm" style={{color:'#f59e0b',fontWeight:700}}>{totals.totalLateMinutes>0?`${totals.totalLateMinutes}m`:'—'}</span></td>
                    <td><span className="mono-sm" style={{color:'#ef4444',fontWeight:700}}>{totals.totalEarlyMinutes>0?`${totals.totalEarlyMinutes}m`:'—'}</span></td>
                    <td><span className="amt" style={{fontWeight:700}}>{fmt(totals.regularPay)}</span></td>
                    <td>{totals.overtimePay>0?<span className="amt purple" style={{fontWeight:700}}>{fmt(totals.overtimePay)}</span>:<span className="amt dim">—</span>}</td>
                    <td>{totalIncentives>0?<span className="amt teal" style={{fontWeight:700}}>{fmt(totalIncentives)}</span>:<span className="amt dim">—</span>}</td>
                    <td>{totals.deductions>0?<span className="amt red" style={{fontWeight:700}}>−{fmt(totals.deductions)}</span>:<span className="amt dim">—</span>}</td>
                    <td className="right"><span className="amt green" style={{fontWeight:700,fontSize:'.9rem'}}>{fmt(totals.netSalary)}</span></td>
                    {!isOwnSalary && <td></td>}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="day-cards">
              {days.map((d, i) => (
                <div key={i} className="day-card">
                  <div className="dc-top">
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <div><div className="dc-date">{fmtDate(d.date)}</div><div className="dc-dow">{dayOfWeek(d.date)}</div></div>
                      <span className={`status-pill ${d.status}`}>{d.status}</span>
                    </div>
                    {!isOwnSalary && <button style={{width:26,height:26,borderRadius:7,border:'1px solid #e4e7f0',background:'white',color:'#9094a8',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}} onClick={() => openEdit(d)}><PencilSquareIcon style={{width:13,height:13}}/></button>}
                  </div>
                  {d.checkIn&&<div className="dc-row"><span className="dc-label">In/Out</span><span className="mono-sm">{fmtTime(d.checkIn)} → {fmtTime(d.checkOut)}</span></div>}
                  <div className="dc-row"><span className="dc-label">Hours</span><span className="mono-sm">{d.workHours?.toFixed(2)}h</span></div>
                  {d.lateMinutes>0&&<div className="dc-row"><span className="dc-label">Late</span><span style={{color:'#f59e0b',fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',fontWeight:600}}>{d.lateMinutes}m</span></div>}
                  {(d.incentive||0)>0&&<div className="dc-row"><span className="dc-label">Incentive</span><span className="amt teal">{fmt(d.incentive)}</span></div>}
                  {d.deductions>0&&<div className="dc-row"><span className="dc-label">Deductions</span><span className="amt red">−{fmt(d.deductions)}</span></div>}
                  <div className="dc-row"><span className="dc-label">Net</span><span className="dc-net">{fmt(d.netDay)}</span></div>
                </div>
              ))}
              <div className="day-total-card">
                <div style={{fontWeight:700,fontSize:'.82rem',color:'#0f1123',marginBottom:8}}>Monthly Total</div>
                <div className="dtc-row"><span className="dtc-label">Total Hours</span><span className="mono-sm" style={{fontWeight:700}}>{totals.totalHours}h</span></div>
                <div className="dtc-row"><span className="dtc-label">Regular Pay</span><span className="amt">{fmt(totals.regularPay)}</span></div>
                {totals.overtimePay>0&&<div className="dtc-row"><span className="dtc-label">OT Pay</span><span className="amt purple">{fmt(totals.overtimePay)}</span></div>}
                {totalIncentives>0&&<div className="dtc-row"><span className="dtc-label">Incentives</span><span className="amt teal">{fmt(totalIncentives)}</span></div>}
                {totals.deductions>0&&<div className="dtc-row"><span className="dtc-label">Deductions</span><span className="amt red">−{fmt(totals.deductions)}</span></div>}
                <div className="dtc-row" style={{borderTop:'1.5px solid #e8ebf4',marginTop:4,paddingTop:8}}>
                  <span className="dtc-label" style={{color:'#0f1123',fontWeight:700}}>Net Salary</span>
                  <span className="amt green" style={{fontSize:'.9rem',fontWeight:700}}>{fmt(totals.netSalary)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Edit Day Modal ── */}
      {editDay && createPortal(
        <div className="modal-backdrop" onClick={() => setEditDay(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Request Edit — {fmtDate(editDay.date)} ({dayOfWeek(editDay.date)})</div>
              <button className="modal-close" onClick={() => setEditDay(null)}><XMarkIcon /></button>
            </div>
            <p className="modal-sub">This will be submitted as a request — admin must approve before the record is changed.</p>
            <div className="edit-grid">
              <div className="edit-field span2">
                <label className="edit-label">Status</label>
                <select className="edit-select" value={editForm.status} onChange={e => setEditForm({...editForm,status:e.target.value})}>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="on-leave">On Leave</option>
                  <option value="holiday">Holiday</option>
                </select>
              </div>
              <div className="edit-field"><label className="edit-label">Check In</label><input type="time" className="edit-input" value={editForm.checkIn} onChange={e=>setEditForm({...editForm,checkIn:e.target.value})} /></div>
              <div className="edit-field"><label className="edit-label">Check Out</label><input type="time" className="edit-input" value={editForm.checkOut} onChange={e=>setEditForm({...editForm,checkOut:e.target.value})} /></div>
              <div className="edit-field"><label className="edit-label">Late Minutes</label><input type="number" min="0" className="edit-input" value={editForm.lateMinutes} onChange={e=>setEditForm({...editForm,lateMinutes:e.target.value})} /></div>
              <div className="edit-field"><label className="edit-label">Early Exit Min</label><input type="number" min="0" className="edit-input" value={editForm.earlyExitMinutes} onChange={e=>setEditForm({...editForm,earlyExitMinutes:e.target.value})} /></div>
              <div className="edit-field span2">
                <label className="edit-label" style={{color:'#8b5cf6'}}>Incentive / Bonus (Rs.)</label>
                <input type="number" min="0" step="0.01" className="edit-input" value={editForm.incentive} onChange={e=>setEditForm({...editForm,incentive:e.target.value})} style={{borderColor:parseFloat(editForm.incentive)>0?'#8b5cf6':undefined}} />
                <span className="edit-hint">One-time bonus or incentive for this day</span>
              </div>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setEditDay(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={submitEditRequest} disabled={saving}><PaperAirplaneIcon />{saving?'Submitting…':'Submit Edit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Add Day Modal ── */}
      {addOpen && createPortal(
        <div className="modal-backdrop" onClick={() => setAddOpen(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Request Add Day — {monthLabel(month)}</div>
              <button className="modal-close" onClick={() => setAddOpen(false)}><XMarkIcon /></button>
            </div>
            <p className="modal-sub">This will be submitted as a request — admin must approve before the record is created.</p>
            <div className="edit-grid">
              <div className="edit-field span2">
                <label className="edit-label">Date *</label>
                <input type="date" className="edit-input" value={addForm.date} min={`${month}-01`} max={`${month}-31`} onChange={e=>setAddForm({...addForm,date:e.target.value})} />
                <span className="edit-hint">Must be within {monthLabel(month)}</span>
              </div>
              <div className="edit-field span2">
                <label className="edit-label">Status</label>
                <select className="edit-select" value={addForm.status} onChange={e=>setAddForm({...addForm,status:e.target.value})}>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                </select>
              </div>
              <div className="edit-field"><label className="edit-label">Check In</label><input type="time" className="edit-input" value={addForm.checkIn} onChange={e=>setAddForm({...addForm,checkIn:e.target.value})} /></div>
              <div className="edit-field"><label className="edit-label">Check Out</label><input type="time" className="edit-input" value={addForm.checkOut} onChange={e=>setAddForm({...addForm,checkOut:e.target.value})} /></div>
              <div className="edit-field"><label className="edit-label">Late Minutes</label><input type="number" min="0" className="edit-input" value={addForm.lateMinutes} onChange={e=>setAddForm({...addForm,lateMinutes:e.target.value})} /></div>
              <div className="edit-field"><label className="edit-label">Early Exit Min</label><input type="number" min="0" className="edit-input" value={addForm.earlyExitMinutes} onChange={e=>setAddForm({...addForm,earlyExitMinutes:e.target.value})} /></div>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setAddOpen(false)}>Cancel</button>
              <button className="btn btn-green" onClick={submitAddRequest} disabled={saving}><PaperAirplaneIcon />{saving?'Submitting…':'Submit Add Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Finalize Modal ── */}
      {finalizeOpen && createPortal(
        <div className="modal-backdrop" onClick={() => setFinalizeOpen(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Request Finalize — {monthLabel(month)}</div>
              <button className="modal-close" onClick={() => setFinalizeOpen(false)}><XMarkIcon /></button>
            </div>
            <p className="modal-sub">
              This will ask admin to finalize <strong>{teacher?.name}</strong>'s salary for {monthLabel(month)}.
              Admin will review and confirm the calculation before saving the record.
            </p>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setFinalizeOpen(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={submitFinalizeRequest} disabled={saving}><PaperAirplaneIcon />{saving?'Submitting…':'Submit Finalize Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}